import { createSlice } from "@reduxjs/toolkit";
import { getFinalPrice } from "../../utils/productPrice";

const cartData = localStorage.getItem("cart");
const cartArray = cartData ? JSON.parse(cartData) : [];

const isSameCartItem = (item, payload) => {
  const itemVariantId = item.variant_id || item.product_variant_id || "";
  const payloadVariantId = payload.variant_id || payload.product_variant_id || "";

  if (itemVariantId || payloadVariantId) {
    return String(itemVariantId) === String(payloadVariantId);
  }

  return (
    item._id === payload._id &&
    (item.color || "") === (payload.color || "") &&
    (item.size || "") === (payload.size || "")
  );
};

const itemFinalPrice = (item) => getFinalPrice(item);

const itemStock = (item) => {
  const stock = Number(item.stock ?? item.available_stock ?? 0);
  return Number.isFinite(stock) ? stock : 0;
};

const clampQuantity = (quantity, item) => {
  const cleanQuantity = Math.max(1, Number(quantity || 1));
  const stock = itemStock(item);

  if (stock > 0) {
    return Math.min(cleanQuantity, stock);
  }

  return cleanQuantity;
};

const allItems = (data) => {
  return data.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
};

const calculateTotal = (data) => {
  return data.reduce(
    (sum, item) => sum + itemFinalPrice(item) * Number(item.quantity || 0),
    0
  );
};

const saveCart = (cart) => {
  localStorage.setItem("cart", JSON.stringify(cart));
};

const normalizeCart = (cart) => {
  return cart
    .map((item) => ({
      ...item,
      quantity: clampQuantity(item.quantity || 1, item),
    }))
    .filter((item) => Number(item.quantity || 0) > 0);
};

const initialCart = normalizeCart(cartArray);

const cartReducer = createSlice({
  name: "cart",
  initialState: {
    cart: initialCart.length > 0 ? initialCart : [],
    items: initialCart.length > 0 ? allItems(initialCart) : 0,
    total: initialCart.length > 0 ? calculateTotal(initialCart) : 0,
  },
  reducers: {
    addCart: (state, { payload }) => {
      const incomingStock = itemStock(payload);
      const requestedQuantity = Number(payload.quantity || 1);

      if (incomingStock <= 0) {
        return;
      }

      const existingItem = state.cart.find((item) =>
        isSameCartItem(item, payload)
      );

      if (existingItem) {
        existingItem.stock = incomingStock;
        existingItem.available_stock = incomingStock;
        existingItem.quantity = clampQuantity(existingItem.quantity + requestedQuantity, existingItem);
      } else {
        const nextItem = {
          ...payload,
          stock: incomingStock,
          available_stock: incomingStock,
          quantity: clampQuantity(requestedQuantity, payload),
        };

        state.cart.push(nextItem);
      }

      state.items = allItems(state.cart);
      state.total = calculateTotal(state.cart);
      saveCart(state.cart);
    },

    incQuantity: (state, { payload }) => {
      const find = state.cart.find((item) => isSameCartItem(item, payload));

      if (find) {
        find.quantity = clampQuantity(find.quantity + 1, find);
        state.items = allItems(state.cart);
        state.total = calculateTotal(state.cart);
        saveCart(state.cart);
      }
    },

    decQuantity: (state, { payload }) => {
      const find = state.cart.find((item) => isSameCartItem(item, payload));

      if (find && find.quantity > 1) {
        find.quantity -= 1;
        state.items = allItems(state.cart);
        state.total = calculateTotal(state.cart);
        saveCart(state.cart);
      }
    },

    removeItem: (state, { payload }) => {
      state.cart = state.cart.filter((item) => !isSameCartItem(item, payload));
      state.items = allItems(state.cart);
      state.total = calculateTotal(state.cart);
      saveCart(state.cart);
    },

    syncCart: (state, { payload }) => {
      const nextCart = normalizeCart(Array.isArray(payload) ? payload : []);
      state.cart = nextCart;
      state.items = allItems(nextCart);
      state.total = calculateTotal(nextCart);
      saveCart(nextCart);
    },

    emptyCart: (state) => {
      state.cart = [];
      state.items = 0;
      state.total = 0;
      localStorage.removeItem("cart");
    },
  },
});

export const { addCart, incQuantity, decQuantity, removeItem, syncCart, emptyCart } =
  cartReducer.actions;

export default cartReducer.reducer;
